/** Reading and writing where each activity sits on a canvas (`OVL-027`). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { CanvasArrangement, LayerPlacement } from "@nebulize/sdk";

import { api } from "@/lib/api";

import type { AddedActivityRow } from "../../types";

/** What an unarranged space answers with. */
export const NOTHING_ARRANGED: CanvasArrangement = { canvases: {} };

/** A placement with nothing optional. */
export interface Placement {
  left: number;
  top: number;
  width: number;
  height: number;
  layer: number;
  /** Whether this activity draws on this canvas at all. Layout, never lifecycle. */
  hidden: boolean;
}

export const FULL_CANVAS: Placement = {
  left: 0,
  top: 0,
  width: 1,
  height: 1,
  layer: 0,
  hidden: false,
};

export const arrangementKeys = {
  of: (spaceId: string) => ["spaces", spaceId, "arrangement"] as const,
};

export function useArrangement(spaceId: string): UseQueryResult<CanvasArrangement> {
  return useQuery({
    queryKey: arrangementKeys.of(spaceId),
    enabled: spaceId !== "",
    queryFn: () => api.get<CanvasArrangement>(`/v1/spaces/${spaceId}/arrangement`),
  });
}

/** Fill in what the wire left out. */
export function placementOf(held: LayerPlacement | undefined): Placement {
  if (held === undefined) return FULL_CANVAS;
  return {
    left: held.left ?? 0,
    top: held.top ?? 0,
    width: held.width ?? 1,
    height: held.height ?? 1,
    layer: held.layer ?? 0,
    hidden: held.hidden ?? false,
  };
}

/** One canvas kind's placements, keyed by added activity id. */
export function placementsByRow(
  arrangement: CanvasArrangement | undefined,
  canvasKind: string,
  rows: readonly AddedActivityRow[],
): Record<string, Placement> {
  const held = arrangement?.canvases?.[canvasKind] ?? {};
  const out: Record<string, Placement> = {};
  for (const row of rows) {
    const found = held[row.name];
    if (found !== undefined) out[row.added_activity_id] = placementOf(found);
  }
  return out;
}

export interface ArrangeEdit {
  canvasKind: string;
  placements: Record<string, Placement>;
}

/** Replace one canvas kind's whole arrangement. An id left out becomes unarranged. */
export function useArrange(
  spaceId: string,
): UseMutationResult<CanvasArrangement, Error, ArrangeEdit> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ canvasKind, placements }: ArrangeEdit) =>
      api.put<CanvasArrangement>(
        `/v1/spaces/${spaceId}/canvases/${encodeURIComponent(canvasKind)}/arrangement`,
        {
          placements: Object.entries(placements).map(([added_activity_id, one]) => ({
            added_activity_id,
            ...one,
          })),
        },
      ),
    // The answer is the whole arrangement, so it lands in the cache rather than costing a refetch
    // the stage would spend showing the placement the drag just replaced.
    onSuccess: (answered) => {
      client.setQueryData(arrangementKeys.of(spaceId), answered);
      void client.invalidateQueries({ queryKey: arrangementKeys.of(spaceId) });
    },
  });
}

/** One step towards the front or the back, keeping every rectangle. Null leaves the list. */
export function reordered(
  order: readonly string[],
  held: Readonly<Record<string, Placement>>,
  moving: string,
  by: number,
): Record<string, Placement> | null {
  const at = order.indexOf(moving);
  const to = at + by;
  if (at < 0 || to < 0 || to >= order.length) return null;
  const next = [...order];
  next.splice(at, 1);
  next.splice(to, 0, moving);
  return Object.fromEntries(
    next.map((id, index) => [id, { ...(held[id] ?? FULL_CANVAS), layer: index }]),
  );
}

/** Drop one layer onto another's place, keeping every rectangle. Null leaves the list. */
export function moved(
  order: readonly string[],
  held: Readonly<Record<string, Placement>>,
  moving: string,
  onto: string,
): Record<string, Placement> | null {
  const at = order.indexOf(moving);
  const to = order.indexOf(onto);
  if (at < 0 || to < 0 || at === to) return null;
  return reordered(order, held, moving, to - at);
}

/** The draw order of one canvas: layer first, the given order behind it. */
export function drawOrder(
  order: readonly string[],
  held: Readonly<Record<string, Placement>>,
): string[] {
  return [...order].sort(
    (left, right) =>
      (held[left]?.layer ?? 0) - (held[right]?.layer ?? 0) ||
      order.indexOf(left) - order.indexOf(right),
  );
}

/** The smallest a placement may be on either axis. */
export const MIN_EXTENT = 0.02;

/** Which handle a drag has hold of: the whole rectangle, a corner, or one edge. */
export type Grip = "move" | "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

/** Every handle a selected layer offers, corners first so a corner sits above an edge. */
export const GRIPS: readonly Grip[] = ["nw", "ne", "sw", "se", "n", "s", "e", "w"];

/** What a modifier key asks of a resize. */
export interface DragOptions {
  /** Shift: keep the rectangle's own ratio. */
  readonly aspect?: boolean;
  /** Alt: hold the centre still, so the opposite edge moves too. */
  readonly centre?: boolean;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/** The rectangle a drag of this grip produces, clamped to the canvas. */
export function dragged(
  start: Placement,
  grip: Grip,
  dx: number,
  dy: number,
  options: DragOptions = {},
): Placement {
  if (grip === "move") {
    return {
      ...start,
      left: clamp(start.left + dx, 0, 1 - start.width),
      top: clamp(start.top + dy, 0, 1 - start.height),
    };
  }

  const west = grip === "nw" || grip === "w" || grip === "sw";
  const east = grip === "ne" || grip === "e" || grip === "se";
  const north = grip === "nw" || grip === "n" || grip === "ne";
  const south = grip === "sw" || grip === "s" || grip === "se";

  const right = start.left + start.width;
  const bottom = start.top + start.height;
  const midX = start.left + start.width / 2;
  const midY = start.top + start.height / 2;
  const baseWidth = Math.max(start.width, MIN_EXTENT);
  const baseHeight = Math.max(start.height, MIN_EXTENT);

  let dw = east ? dx : west ? -dx : 0;
  let dh = south ? dy : north ? -dy : 0;

  if (options.aspect === true) {
    const ratio = baseWidth / baseHeight;
    if ((west || east) && (north || south)) {
      if (Math.abs(dw) >= Math.abs(dh * ratio)) dh = dw / ratio;
      else dw = dh * ratio;
    } else if (west || east) {
      dh = dw / ratio;
    } else {
      dw = dh * ratio;
    }
  }

  const grow = options.centre === true ? 2 : 1;
  const roomWide =
    options.centre === true ? Math.min(midX, 1 - midX) * 2 : west ? right : 1 - start.left;
  const roomHigh =
    options.centre === true ? Math.min(midY, 1 - midY) * 2 : north ? bottom : 1 - start.top;
  const maxWidth = Math.max(roomWide, MIN_EXTENT);
  const maxHeight = Math.max(roomHigh, MIN_EXTENT);

  let width: number;
  let height: number;

  if (options.aspect === true) {
    // The ratio survives the clamp because both axes take the one scale, not two.
    const low = Math.max(MIN_EXTENT / baseWidth, MIN_EXTENT / baseHeight);
    const high = Math.max(Math.min(maxWidth / baseWidth, maxHeight / baseHeight), low);
    const scale = clamp((baseWidth + dw * grow) / baseWidth, low, high);
    width = baseWidth * scale;
    height = baseHeight * scale;
  } else {
    width = clamp(start.width + dw * grow, MIN_EXTENT, maxWidth);
    height = clamp(start.height + dh * grow, MIN_EXTENT, maxHeight);
  }

  const left = options.centre === true ? midX - width / 2 : west ? right - width : start.left;
  const top = options.centre === true ? midY - height / 2 : north ? bottom - height : start.top;

  return {
    ...start,
    left: clamp(left, 0, 1 - width),
    top: clamp(top, 0, 1 - height),
    width,
    height,
  };
}

/** How far one arrow key moves or resizes a layer, and the coarse step Shift asks for. */
export const NUDGE = 0.005;
export const NUDGE_COARSE = 0.05;

const ARROWS: Readonly<Record<string, { x: number; y: number }>> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 },
};

/** What the modifiers held with an arrow key ask for. */
export interface KeyStep {
  /** Shift: the coarse step. */
  readonly coarse?: boolean;
  /** Alt: resize from the south east rather than move. */
  readonly resize?: boolean;
}

/** The rectangle an arrow key produces, or null when the key is not an arrow. */
export function keyed(start: Placement, key: string, step: KeyStep = {}): Placement | null {
  const way = ARROWS[key];
  if (way === undefined) return null;
  const by = step.coarse === true ? NUDGE_COARSE : NUDGE;
  return dragged(start, step.resize === true ? "se" : "move", way.x * by, way.y * by);
}

/** Every placement but this one, which is how one activity goes back to unarranged. */
export function without(
  held: Readonly<Record<string, Placement>>,
  addedActivityId: string,
): Record<string, Placement> {
  return Object.fromEntries(
    Object.entries(held).filter(([id]) => id !== addedActivityId),
  );
}
